import { BillingIntervalEnum, CurrencyTypeEnum } from "@/api/common-types";
import benefitCheckmarkIconBlack from "@/assets/img/icon-checkmark-benefit-black.svg";
import iconWidgetSelectedBlack from "@/assets/img/icon-widget-selected-dark.svg";
import { BCard, BCardText } from "@/lib/typed-bootstrap";
import { ILocalizedBenefits } from "@/models/benefit";
import {
  IFreeTiers,
  IPriceState,
  IProductState
} from "@/models/product-bundle";
import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";
import { Button, ButtonThemeEnum } from "../shared/Button.component";
import styles from "./BundleWidget.component.module.scss";

@Component
export default class BundleWidget extends Vue {
  _tsx!: tsx.DeclareProps<{
    value: BundleWidget["value"];
    product: BundleWidget["product"];
    billingInterval: BundleWidget["billingInterval"];
    freeTiers?: BundleWidget["freeTiers"];
  }> &
    tsx.DeclareOnEvents<{
      onChooseBundle: void;
    }>;

  @Prop({ required: true }) value: boolean;
  @Prop({ required: true }) product: IProductState;
  @Prop() billingInterval: BillingIntervalEnum;
  @Prop() freeTiers?: IFreeTiers;
  @Emit("chooseBundle") onChooseBundle() {}

  get internalValue(): boolean {
    return this.value;
  }

  set internalValue(value: boolean) {
    this.$emit("input", value);
  }

  get pricings() {
    return this.product.pricings[this.billingInterval] ?? [];
  }

  get isAnnualPricing() {
    return this.billingInterval === BillingIntervalEnum.YEAR;
  }

  get selectedPricingIndex() {
    return this.product.selectedPricingIndex < this.pricings.length
      ? this.product.selectedPricingIndex
      : 0;
  }

  get selectedPricing(): IPriceState | undefined {
    return this.pricings[this.selectedPricingIndex];
  }

  get localizedBenefits() {
    return (
      this.product.benefits[
        this.$i18n.locale.toUpperCase() as keyof ILocalizedBenefits
      ] ??
      this.product.benefits.EN ??
      []
    );
  }

  get firstBenefit() {
    return this.localizedBenefits[0] ?? "";
  }

  get benefitList(): string[] {
    return this.localizedBenefits.slice(1);
  }

  get billingIntervalString() {
    return {
      [BillingIntervalEnum.DAY]: `${this.$t("billing_interval_day")}`,
      [BillingIntervalEnum.MONTH]: `${this.$t("billing_interval_month")}`,
      [BillingIntervalEnum.WEEK]: `${this.$t("billing_interval_week")}`,
      [BillingIntervalEnum.YEAR]: `${this.$t("billing_interval_year")}`,
      [BillingIntervalEnum.ONETIME]: ""
    };
  }

  get buttonCopy() {
    return this.$t("global_cta_choose").toString();
  }

  get price(): number {
    return (this.selectedPricing?.price ?? 0) / this.priceDivisor;
  }

  get priceDivisor(): number {
    return this.selectedPricing?.billingInterval === BillingIntervalEnum.YEAR
      ? 12
      : 1;
  }

  get priceCurrencyString(): string {
    return this.selectedPricing?.currency === CurrencyTypeEnum.USD ? "$" : "€";
  }

  get priceDecimal(): number {
    const integerPart = this.priceInteger;
    const decimalPart = this.price - integerPart;
    return Math.round(decimalPart * 100) / 100;
  }

  get priceDecimalString(): string {
    return this.priceDecimal > 0
      ? this.priceDecimal
          .toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
          .substring(1)
      : "";
  }

  get priceInteger(): number {
    return Math.trunc(this.price);
  }

  get priceIntegerString(): string {
    return this.priceInteger.toLocaleString();
  }

  get priceTimeframe(): string {
    return this.selectedPricing?.recurring &&
      this.selectedPricing?.billingInterval &&
      this.selectedPricing?.billingInterval !== BillingIntervalEnum.ONETIME
      ? `/ ${this.billingIntervalString[BillingIntervalEnum.MONTH]}`
      : "";
  }

  get priceSectionHeader() {
    return this.$t("bundle_widget_caption");
  }

  get unitAmountLocaleString(): string {
    return this.selectedPricing?.unitAmount
      ? this.selectedPricing.unitAmount.toLocaleString()
      : "free";
  }

  get currentLangFreeTiers() {
    if (!this.freeTiers) return [];

    return (
      this.freeTiers[this.$i18n.locale.toUpperCase() as keyof IFreeTiers] || []
    );
  }

  get checkmarkBenefitIconSrc() {
    return benefitCheckmarkIconBlack;
  }

  get bundleSelectedSrc() {
    return iconWidgetSelectedBlack;
  }

  getBodyClass() {
    const classes = [styles.cardBody];
    if (this.value) {
      classes.push(styles.active);
    }
    return classes.join(" ");
  }

  getChooseButtonClass() {
    const classes = [styles.chooseButton];
    if (this.value) {
      classes.push(styles.hidden);
    }
    return classes.join(" ");
  }

  render() {
    return (
      <BCard bodyClass={this.getBodyClass()} class={`${styles.card} col-md-3`}>
        {this.value && (
          <div class={styles.activeIconPosition}>
            <img src={this.bundleSelectedSrc} alt="Selected" />
          </div>
        )}
        <BCardText class={styles.cardText}>
          <div class={styles.bundleName}>{this.product.displayName}</div>
          <div class={styles.priceSection}>
            <div class={styles.price}>
              <span
                class={[
                  styles.priceInteger,
                  this.isAnnualPricing && styles.highlight
                ]}
              >
                {this.priceIntegerString}
              </span>
              <div class={styles.priceAmendments}>
                <span
                  class={[
                    styles.priceDecimal,
                    this.isAnnualPricing && styles.highlight
                  ]}
                >
                  {this.priceDecimalString + this.priceCurrencyString}
                </span>
                <span class={styles.priceTimeframe}>{this.priceTimeframe}</span>
              </div>
            </div>
          </div>
          <div class={styles.bundleDescription}>{this.firstBenefit}</div>
          <div class={styles.benefits}>
            <div class={styles.benefitsHeader}>
              {this.$t("bundle_benefits")}
            </div>
            {this.benefitList.map((benefit, idx) =>
              benefit && benefit.trim().length > 0 ? (
                <div class={styles.benefit} key={`benefit-${idx}`}>
                  <img
                    src={this.checkmarkBenefitIconSrc}
                    class={styles.benefitCheckmark}
                    alt=""
                  />
                  <span>{benefit}</span>
                </div>
              ) : null
            )}
          </div>
        </BCardText>
        <Button
          type="button"
          theme={ButtonThemeEnum.outline}
          class={this.getChooseButtonClass()}
          onClick={(e: Event) => {
            e.preventDefault();
            this.onChooseBundle();
          }}
        >
          {this.buttonCopy}
        </Button>
      </BCard>
    );
  }
}
